import { useContext, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { cn } from '@/utlis/cn'
import { logger } from '@/services/Logger'
import { Button } from '@/UIComponents2/Button'
import DiagonalTabs from '@/components/DiagonalTabs'
import { AudioInputSelectionPanel } from '@/components/AudioInputSelectionPanel/AudioInputSelectionPanel'
import { getRoomName } from '@/RoomContext'
import RightArrowIcon from '@/CustomIcons/RightArrowIcon'
import { BasicAVTab } from './internal/BasicAVTab'
import { useMediaStreamCleanup } from './internal/useMediaStreamCleanup'
import { TextInput } from '@/UIComponents2/TextInput'
import useJoinRoom from '@/services/useJoinRoom'
import { parseJoinRoomLink } from '@/services/JoinRoomLink'
import { ClientType } from '@/UserContext'
import useDimensionObserver from '@/services/useDimensionObserver'
import { useRecycleSessionId } from './internal/useRecycleSessionId'
import { Trans, useTranslation } from 'react-i18next'
import { PageLayout } from './internal/PageLayout'
import { Popover } from 'radix-ui'
import { formatTimestamp } from '@/utils'
import moment from 'moment'
import { isElectron } from '@/utlis/isElectron'
import { title_bar_height_px } from '@/services/Constants'
import { PortalContext } from '@/UIComponents2/PortalContext'
import { persistent } from '@/services/LocalStorage'
import { DashboardRoom } from '@/AppContext'
import useMiscRoomActions from '@/services/useMiscRoomActions'
import { LaunchAppButton } from '../UnauthorizedPages/GuestJoinPage/internal/LaunchAppButton'
import { env } from '@/services/EnvironmentVariables'

const log = logger.createChildLogger({ name: 'JoinSessionPage' })

export function JoinSessionPage() {
    const { t } = useTranslation()
    useMediaStreamCleanup()
    useRecycleSessionId()

    const [width, setWidth] = useState(690)
    const { nodeRef: contentRef } = useDimensionObserver({
        onResize: (width) => {
            setWidth(width)
        },
    })

    const tabContentStyle = 'flex flex-col items-center py-4 md:py-8 lg:py-12 px-2 md:px-4 lg:px-10'

    return (
        <PageLayout rightContent={<FormSection />} ref={contentRef}>
            <DiagonalTabs
                widthFitTabs
                childrenByTabIndex={[
                    <div className={tabContentStyle}>
                        <BasicAVTab />
                    </div>,
                    <div className={tabContentStyle}>
                        <div className="w-full min-h-[400px]">
                            <AudioInputSelectionPanel />
                        </div>
                    </div>,
                ]}
                containerHeight={700}
                containerWidth={width}
                responsive
                tabs={[
                    { id: 'av', label: t('Audio & Video') },
                    { id: 'ad', label: t('Audio Devices') },
                ]}
                key="host-session-tabs"
            />
        </PageLayout>
    )
}

function FormSection() {
    const { t } = useTranslation()
    const { openSettingsModal } = useMiscRoomActions()

    const [password, setPassword] = useState('')
    const [metadata, setMetadata] = useState<Record<string, string>>({})
    const [showPasswordRequiredError, setShowPasswordRequiredError] = useState(false)
    const { code: joinCodeParam } = useParams<{ code: string }>()

    const {
        roomData,
        joincode,
        setJoincode,
        joinRoomWithCode,
        joinRoomLoading,
        joinSessionErrorMessage,
        joinSessionErrorCode,
        showPasswordPrompt,
    } = useJoinRoom()

    useEffect(() => {
        const { error, code, metadata, clientType } = parseJoinRoomLink(window.location.toString(), joinCodeParam)
        if (metadata) {
            setMetadata(metadata)
        }
        if (code) {
            joinRoomWithCode(code, undefined, metadata, undefined, clientType as ClientType, true)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return (
        <>
            <div className="hidden md:block">
                <div className="t-title pt-[40px] pb-[10px]">
                    <Trans i18nKey="Ready to <1 /> Collaborate?" t={t}>
                        Join Session
                    </Trans>
                </div>
                {roomData && (
                    <Trans
                        t={t}
                        i18nKey="Joining {{roomName}} <0>({{code}})</0>"
                        values={{ roomName: getRoomName(roomData.room), code: joincode }}
                        components={[<span className="t-base-light opacity-65" />]}
                    />
                )}
            </div>

            {!roomData && <JoinCodeInput joincode={joincode} setJoincode={setJoincode} />}
            {showPasswordPrompt && (
                <div className="pb-5">
                    <PasswordSection
                        password={password}
                        setPassword={setPassword}
                        showIncorrectPasswordError={joinSessionErrorCode === 'password_mismatch'}
                        showPasswordRequiredError={showPasswordRequiredError}
                    />
                </div>
            )}
            <Button
                variant="primary"
                className={cn('relative')}
                onClick={() => {
                    if (showPasswordPrompt && password.length === 0) {
                        setShowPasswordRequiredError(true)
                        return
                    }
                    joinRoomWithCode(joincode, password, metadata)
                    setShowPasswordRequiredError(false)
                }}
                disabled={joinRoomLoading}
            >
                {t('Join')}
                <RightArrowIcon className="absolute right-4" />
            </Button>
            <Button variant="glow" onClick={() => openSettingsModal('audio')}>
                {t('Detailed Settings')}
            </Button>

            {/* TODO: design */}
            {joinSessionErrorMessage && joinSessionErrorCode !== 'password_mismatch' && (
                <div className="text-red-500">{joinSessionErrorMessage}</div>
            )}
            {!env.IS_ELECTRON && (
                <div className="pt-26">
                    <LaunchAppButton joinCode={joincode} joinLinkMetadata={metadata} />
                </div>
            )}
        </>
    )
}

function JoinCodeInput({ joincode, setJoincode }: { joincode: string; setJoincode: (code: string) => void }) {
    const [joinPopOpen, setJoinPopOpen] = useState(false)
    const recentRooms = persistent.getArray<DashboardRoom>('recentRooms') || []
    const { t } = useTranslation()
    const portalContainer = useContext(PortalContext)
    const [popoverWidth, setPopoverWidth] = useState(0)
    const { nodeRef: inputRef } = useDimensionObserver({
        onResize: (width) => {
            setPopoverWidth(width)
        },
    })

    const contentMaxHeight = isElectron()
        ? { maxHeight: `calc(var(--radix-popover-content-available-height) - ${title_bar_height_px}px)` }
        : { maxHeight: 'var(--radix-popover-content-available-height)' }

    const onPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
        const text = e.clipboardData.getData('Text')
        if (!text) {
            return
        }
        try {
            const url = new URL(text)
            const code = url.searchParams.get('code') || url.searchParams.get('join-room-code')
            if (code && typeof code === 'string' && code.length > 0) {
                setJoincode(code)
            }
        } catch (e) {
            setJoincode(text)
        } finally {
            e.preventDefault()
        }
        setJoinPopOpen(false)
    }

    return (
        <Popover.Root open={joinPopOpen}>
            <Popover.Anchor ref={inputRef}>
                <TextInput
                    label={t('Session Link')}
                    value={joincode}
                    onChange={(str) => setJoincode(str)}
                    placeholder={t('Paste link')}
                    onPaste={onPaste}
                    onFocus={() => recentRooms.length > 0 && setJoinPopOpen(true)}
                    onBlur={(e) => {
                        if (e.relatedTarget?.id !== 'join-room-popover-content') {
                            setJoinPopOpen(false)
                        }
                    }}
                    disableAutocomplete
                />
            </Popover.Anchor>
            <Popover.Portal container={portalContainer}>
                <Popover.Content
                    className={cn(
                        'overflow-hidden',
                        'bg-muse-menu',
                        'bg-blend-luminosity',
                        'shadow-[inset_2px_4px_16px_0px_rgba(255,255,255,0.1)]',
                        'border-muse-toplit',
                        'rounded-[16px]',
                        'flex flex-col'
                    )}
                    style={{ ...contentMaxHeight, width: popoverWidth }}
                    onOpenAutoFocus={(e) => e.preventDefault()}
                    collisionPadding={8}
                    id="join-room-popover-content"
                >
                    <div className="overflow-y-auto h-full">
                        {recentRooms.map((room, index) => (
                            <>
                                {index > 0 && <div className="h-[1px] bg-white/10" />}
                                <div
                                    key={room.joinCode}
                                    className="p-2 flex flex-col gap-2 cursor-pointer hover:bg-white/10 focus-visible:bg-white/10"
                                    onClick={() => {
                                        setJoincode(room.joinCode)
                                        setJoinPopOpen(false)
                                    }}
                                >
                                    <div className="t-base-medium">{room.name || 'Untitled'}</div>
                                    <div className="t-sm-light">
                                        <div>
                                            {t('Session code')}: {`${room.joinCode}` || ''}
                                        </div>
                                        <div>
                                            {t('Created', { context: 'date' })}:{' '}
                                            {formatTimestamp(moment(room.createdAt).toDate())}
                                        </div>
                                    </div>
                                </div>
                            </>
                        ))}
                    </div>
                </Popover.Content>
            </Popover.Portal>
        </Popover.Root>
    )
}

function PasswordSection({
    password,
    showIncorrectPasswordError,
    showPasswordRequiredError,
    setPassword,
}: {
    password: string
    showIncorrectPasswordError: boolean
    showPasswordRequiredError: boolean
    setPassword: (password: string) => void
}) {
    const { t } = useTranslation()
    const [errorMessage, setErrorMessage] = useState('')

    useEffect(() => {
        if (showIncorrectPasswordError) {
            setErrorMessage(t('Incorrect password'))
        } else if (showPasswordRequiredError) {
            setErrorMessage(t('Password is required'))
        }
    }, [showIncorrectPasswordError, showPasswordRequiredError])

    const onChange = (str: string) => {
        setPassword(str)
        if (str.length === 0) {
            setErrorMessage(t('Password is required'))
        } else {
            setErrorMessage('')
        }
    }

    return (
        <TextInput
            label={t('Session Password')}
            value={password}
            onChange={onChange}
            placeholder={t('Enter password')}
            error={errorMessage}
        />
    )
}
